/**
 * go-code-factory WebSocket client.
 *
 * Connects directly to CF BFF at GET /api/v1/ws (VITE_ARAF_FACTORY_URL).
 * Protocol:
 *   Client → Server: { action: "subscribe"|"unsubscribe"|"ping", runIds: string[] }
 *   Server → Client: FactoryWSEvent
 *
 * The server replays buffered tokens for newly subscribed runs automatically.
 * This module owns the single WS connection; all consumers call subscribe/unsubscribe.
 */

import type { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useFactoryWsStore } from '../stores/factoryWsStore';
import type { FactoryWSEvent } from '../types/pipeline';

export interface FactoryWsContextValue {
  subscribe: (runIds: string[]) => void;
  unsubscribe: (runIds: string[]) => void;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
}

export const FactoryWsContext = createContext<FactoryWsContextValue>({
  subscribe: () => undefined,
  unsubscribe: () => undefined,
  connectionStatus: 'disconnected',
});

export function useFactoryWs(): FactoryWsContextValue {
  return useContext(FactoryWsContext);
}

// ── Connection singleton ───────────────────────────────────────────────────────

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;

type DispatchFn = (event: FactoryWSEvent, queryClient: ReturnType<typeof useQueryClient>) => void;

export interface FactoryWsConnection {
  subscribe: (runIds: string[]) => void;
  unsubscribe: (runIds: string[]) => void;
  destroy: () => void;
}

function buildWsUrl(token: string): string {
  const http = (import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined) ?? '';
  const ws = http.replace(/^https?/, (m) => (m === 'https' ? 'wss' : 'ws'));
  return `${ws}/api/v1/ws?token=${encodeURIComponent(token)}`;
}

export function createFactoryWsConnection(
  getToken: () => Promise<string | null>,
  onStatusChange: (s: 'disconnected' | 'connecting' | 'connected' | 'error') => void,
  onEvent: DispatchFn,
  queryClient: ReturnType<typeof useQueryClient>,
): FactoryWsConnection {
  let ws: WebSocket | null = null;
  let destroyed = false;
  let backoffIndex = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const activeSubs = new Set<string>();

  function send(msg: object): void {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }

  async function connect(): Promise<void> {
    if (destroyed) return;
    onStatusChange('connecting');
    const token = await getToken();
    if (!token || destroyed) {
      onStatusChange('error');
      return;
    }

    ws = new WebSocket(buildWsUrl(token));

    ws.onopen = () => {
      backoffIndex = 0;
      onStatusChange('connected');
      if (activeSubs.size > 0) {
        send({ action: 'subscribe', runIds: Array.from(activeSubs) });
      }
    };

    ws.onmessage = (evt) => {
      try {
        const event = JSON.parse(evt.data as string) as FactoryWSEvent;
        onEvent(event, queryClient);
      } catch {
        // ignore malformed frames
      }
    };

    ws.onerror = () => {
      onStatusChange('error');
    };

    ws.onclose = () => {
      if (destroyed) return;
      onStatusChange('disconnected');
      const delay = BACKOFF_MS[Math.min(backoffIndex, BACKOFF_MS.length - 1)];
      backoffIndex++;
      reconnectTimer = setTimeout(() => void connect(), delay);
    };
  }

  void connect();

  return {
    subscribe(runIds: string[]) {
      for (const id of runIds) activeSubs.add(id);
      send({ action: 'subscribe', runIds });
    },
    unsubscribe(runIds: string[]) {
      for (const id of runIds) activeSubs.delete(id);
      send({ action: 'unsubscribe', runIds });
    },
    destroy() {
      destroyed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
      ws = null;
    },
  };
}

// ── Default event dispatcher ──────────────────────────────────────────────────

export function dispatchFactoryWsEvent(
  event: FactoryWSEvent,
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  const wsStore = useFactoryWsStore.getState();

  switch (event.event) {
    case 'stage_token': {
      if (!event.pipelineRunId || !event.data) break;
      try {
        const parsed = JSON.parse(event.data) as { chunk?: string };
        if (parsed.chunk) {
          wsStore.appendToken(event.pipelineRunId, parsed.chunk);
        }
      } catch {
        // raw string fallback
        wsStore.appendToken(event.pipelineRunId, event.data);
      }
      break;
    }

    case 'stage_started':
    case 'stage_failed': {
      if (event.pipelineRunId) {
        void queryClient.invalidateQueries({ queryKey: ['factory', 'run', event.pipelineRunId] });
        void queryClient.invalidateQueries({
          queryKey: ['factory', 'stages', event.pipelineRunId],
        });
      }
      break;
    }

    case 'stage_review': {
      if (event.pipelineRunId) {
        void queryClient.invalidateQueries({ queryKey: ['factory', 'run', event.pipelineRunId] });
        void queryClient.invalidateQueries({
          queryKey: ['factory', 'stages', event.pipelineRunId],
        });
        if (event.stageExecId) {
          wsStore.setReviewingStageExecId(event.pipelineRunId, event.stageExecId);
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['factory', 'approvals'] });
      break;
    }

    case 'run_completed':
    case 'run_failed': {
      const runId = event.runId ?? event.pipelineRunId;
      if (runId) {
        void queryClient.invalidateQueries({ queryKey: ['factory', 'run', runId] });
        void queryClient.invalidateQueries({ queryKey: ['factory', 'runs'] });
      }
      void queryClient.invalidateQueries({ queryKey: ['factory', 'approvals'] });
      if (event.pipelineRunId) {
        wsStore.clearReviewingStageExecId(event.pipelineRunId);
      }
      if (event.event === 'run_completed' && event.pipelineRunId) {
        wsStore.clearBuffer(event.pipelineRunId);
      }
      break;
    }

    default:
      break;
  }
}
